import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChannelHistogramPreview,
  getNormalizationSummary,
  getSlideChannelHistogramPreview,
  getSlideChannelHistogramPreviewQueryKey,
  HistogramFlavorPreview,
  MarkerNormalizationObservation,
  SlideChannelHistogramPreviewResponse,
  SlideChannelNormalizationResultSummary,
  SlideNormalizationSummary,
} from 'api/slideMultiplexChannelNormalizations';
import { getSlidesViewerNavigationPrefetchData } from 'api/slides';
import PlotlyChartActions from 'components/Plotly/PlotlyChartActions';
import ThemedPlotlyFigure from 'components/Plotly/ThemedPlotlyFigure';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import {
  encodeMarkerChannelHistograms,
  markerChannelHistogramsQueryParam,
} from 'components/Procedure/SlideControls/Multiplex/channelHistogramParams';
import { useFilteredCaseSlideThumbnailsData } from 'components/Procedure/useFilteredCaseIds';
import { PlotlyFigure } from 'interfaces/agentSession';
import { Slide, SlidePrefetchData } from 'interfaces/slide';
import { compact, find, first, groupBy, map, max, orderBy, sortBy, uniq, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { exportProcedureMetadataCsv } from 'utils/exports';
import { useCasesParams } from 'utils/useCasesParams';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { NormalizationCoverageSummaryPanel, useNormalizationCoverageSummary } from './NormalizationCoverageSummary';
import {
  buildChannelHistogramPreviewFigure,
  buildMarkerNormalizationFigureSets,
  buildMultichannelRangeOverviewFigure,
  buildMultichannelZScoreScatterFigure,
  getHistogramFlavorColor,
  getHistogramFlavorEntries,
  getHistogramFlavorLabel,
} from './normalizationFigures';
import { buildMarkerNormalizationInsights, MarkerNormalizationSummary } from './normalizationInsights';

interface QCMarkerNormalizationInsightsProps {
  studyId: string;
  markerIds?: string[];
  scope?: 'dataset' | 'study';
}

interface SlideMarkerPanelSummary {
  slideId: string;
  markerIds: string[];
  orderedMarkerIds: string[];
  panelKey: string;
  orderKey: string;
}

interface PanelOrderVariant {
  variantId: string;
  orderKey: string;
  orderedMarkerIds: string[];
  slideCount: number;
  slideIds: string[];
}

interface CohortPanelGroup {
  panelId: string;
  panelKey: string;
  markerIds: string[];
  representativeOrderMarkerIds: string[];
  orderVariantCount: number;
  orderVariants: PanelOrderVariant[];
  slideCount: number;
  slideIds: string[];
}

interface SlideMarkerStructureExportDetails {
  markerOrder: string;
  orderVariantCount: number;
  orderVariantId: string;
  panelId: string;
  panelMarkers: string;
  panelSlideCount: number;
}

interface MarkerOption {
  label: string;
  value: string;
}

interface HistogramSlideOption {
  label: string;
  value: string;
  observation: MarkerNormalizationObservation;
}

interface HistogramFlavorOption {
  flavor: string;
  label: string;
  color: string;
}

const metricFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});
const pixelCountFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const minimumOverviewChartHeight = 340;
const overviewChartHeightPerMarker = 36;
const overviewChartHeightPadding = 160;
const minimumLoadingMarkerCount = 4;
const multiplexViewerIndex = 0;
const histogramPreviewBinCount = 2 ** 16;
const histogramPreviewFigureHeight = 320;
const histogramPreviewStaleTimeMs = 5 * 60 * 1000;
const idleHistogramPrefetchLimit = 8;
const markerDrillDownHeaderSx = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  py: 1.5,
  mb: 2,
  backgroundColor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
};
const markerQcTableStickyHeaderSx = {
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: 'background.paper',
  },
};

const formatMetric = (value: number) => metricFormatter.format(value);
const formatMetricBand = (lowerValue: number, upperValue: number) =>
  `${formatMetric(lowerValue)} - ${formatMetric(upperValue)}`;
const formatMarkerOrder = (markerIds: string[], getMarkerLabel: (markerId: string) => string) =>
  markerIds.map((markerId) => getMarkerLabel(markerId)).join(' -> ');
const getHistogramFlavorPreviews = (histogramPreview?: ChannelHistogramPreview | null): HistogramFlavorPreview[] => {
  if (!histogramPreview) {
    return [];
  }

  return [...Object.values(histogramPreview.flavors || {}), histogramPreview.raw, histogramPreview.nsm].filter(
    Boolean
  ) as HistogramFlavorPreview[];
};
const getHistogramBitDepthLabel = (histogramPreview?: ChannelHistogramPreview | null) => {
  const bitDepth = histogramPreview?.bitDepth;

  if (bitDepth === 8 || bitDepth === 16) {
    return `${bitDepth}-bit`;
  }

  const sourceBinCount = max(
    map(getHistogramFlavorPreviews(histogramPreview), (flavorPreview) => flavorPreview.sourceBinCount)
  );

  if (sourceBinCount === 2 ** 8) {
    return '8-bit';
  }

  if (sourceBinCount === 2 ** 16) {
    return '16-bit';
  }

  return sourceBinCount ? `${sourceBinCount} source bins` : null;
};

const getHistogramPreviewRequestParams = (observation: MarkerNormalizationObservation) => ({
  slideId: observation.slideId,
  channelKey: observation.channelKey,
  previewBinCount: histogramPreviewBinCount,
});

const scheduleIdleWork = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const browserWindow = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof browserWindow.requestIdleCallback === 'function') {
    const idleHandle = browserWindow.requestIdleCallback(callback);

    return () => {
      browserWindow.cancelIdleCallback?.(idleHandle);
    };
  }

  const timeoutHandle = setTimeout(callback, 500);

  return () => {
    clearTimeout(timeoutHandle);
  };
};

const getHistogramTotalPixelsLabel = (
  histogramPreview: ChannelHistogramPreview | null | undefined,
  visibleFlavorNames: string[]
) => {
  if (!histogramPreview) {
    return null;
  }

  const visibleFlavorSet = visibleFlavorNames.length ? new Set(visibleFlavorNames) : null;
  const totalEntries = getHistogramFlavorEntries(histogramPreview)
    .filter(([flavor]) => !visibleFlavorSet || visibleFlavorSet.has(flavor))
    .filter(([, flavorPreview]) => Number.isFinite(flavorPreview.totalCount));

  if (!totalEntries.length) {
    return null;
  }

  const uniqueTotals = uniq(map(totalEntries, ([, flavorPreview]) => flavorPreview.totalCount));

  if (uniqueTotals.length === 1) {
    return `Total pixels: ${pixelCountFormatter.format(uniqueTotals[0])}`;
  }

  return `Total pixels: ${totalEntries
    .map(
      ([flavor, flavorPreview]) =>
        `${getHistogramFlavorLabel(flavor)} ${pixelCountFormatter.format(flavorPreview.totalCount)}`
    )
    .join(' · ')}`;
};

const getHistogramExportFileName = ({
  markerLabel,
  observation,
}: {
  markerLabel: string;
  observation: MarkerNormalizationObservation | null;
}) => {
  const baseName = observation
    ? `${markerLabel}-${observation.slideId}-channel-${observation.channelKey}-histogram`
    : `${markerLabel}-histogram`;
  const sanitizedName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return sanitizedName || 'channel-histogram';
};

const isDnaMarkerId = (markerId: string) => {
  const normalizedMarkerId = markerId.toLowerCase();
  return ['dapi', 'hoechst', 'syto'].includes(normalizedMarkerId) || normalizedMarkerId.includes('dna');
};

const buildMarkerChipLabel = (label: string, dotColor?: string) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {dotColor && (
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: dotColor }} />
    )}
    <Box component="span">{label}</Box>
  </Box>
);

interface MultichannelOutlierCandidate {
  slideId: string;
  outlierMarkerIds: string[];
  outlierMarkerCount: number;
  maxAbsoluteZScore: number;
}

interface PlotlyPointLinkConfig {
  type?: string;
  customdataIndex?: number;
  markerIdCustomdataIndex?: number;
}

interface PlotlyClickPoint {
  customdata?: unknown;
}

interface PlotlyClickEvent {
  points?: PlotlyClickPoint[];
}

const buildScopeCasesParams = ({
  casesParams,
  scope,
  studyId,
}: {
  casesParams: ReturnType<typeof useCasesParams>['casesParams'];
  scope: 'dataset' | 'study';
  studyId: string;
}) => {
  if (scope === 'study') {
    return {
      studyId,
      labId: casesParams.labId,
      slidesMode: false,
      resultsMode: casesParams.resultsMode,
      filters: {
        studyId,
      },
    };
  }

  return {
    ...casesParams,
    studyId,
    filters: {
      ...(casesParams.filters || {}),
      studyId,
    },
  };
};

const stripPlotTitle = (figure?: PlotlyFigure | null): PlotlyFigure | null => {
  if (!figure) {
    return null;
  }

  const layout = { ...(figure.layout || {}) };
  delete layout.title;

  return {
    ...figure,
    layout,
  };
};

const getPointLinkConfig = (figure?: PlotlyFigure | null): PlotlyPointLinkConfig | null => {
  if (!figure) {
    return null;
  }

  const layout = (figure.layout as Record<string, unknown>) || {};
  const meta = (layout.meta as Record<string, unknown>) || {};
  const pointLink = meta.pointLink;

  if (!pointLink || typeof pointLink !== 'object') {
    return null;
  }

  return pointLink as PlotlyPointLinkConfig;
};

const getPlotlyPointCustomdata = (event: unknown): unknown => {
  return (event as PlotlyClickEvent | undefined)?.points?.[0]?.customdata;
};

const getPlotlyCustomdataString = (customdata: unknown, index: number | undefined): string | null => {
  if (typeof customdata === 'string') {
    const normalizedValue = customdata.trim();
    return normalizedValue || null;
  }

  if (!Array.isArray(customdata) || typeof index !== 'number') {
    return null;
  }

  const candidateValue = customdata[index];
  if (typeof candidateValue !== 'string') {
    return null;
  }

  const normalizedValue = candidateValue.trim();
  return normalizedValue || null;
};

const encodeMarkerChannelToggle = ({
  visibleMarkerIds,
  toggleMarkerIds,
}: {
  visibleMarkerIds: string[];
  toggleMarkerIds: string[];
}) => {
  const uniqueVisibleMarkerIds = uniq(compact(visibleMarkerIds));
  const uniqueToggleMarkerIds = uniq(compact(toggleMarkerIds));

  if (!uniqueVisibleMarkerIds.length || !uniqueToggleMarkerIds.length) {
    return null;
  }

  const encodedMarkerChannelToggle = CompressedJsonParam.encode(
    uniqueToggleMarkerIds.reduce<Record<string, Record<number, boolean>>>((markerToggles, markerId) => {
      markerToggles[markerId] = {
        [multiplexViewerIndex]: uniqueVisibleMarkerIds.includes(markerId),
      };

      return markerToggles;
    }, {})
  );

  return Array.isArray(encodedMarkerChannelToggle) ? encodedMarkerChannelToggle[0] || null : encodedMarkerChannelToggle;
};

const addSearchParamsToUrl = (baseUrl: string, params: Record<string, string | null | undefined>) => {
  const [pathname, existingSearch = ''] = baseUrl.split('?');
  const searchParams = new URLSearchParams(existingSearch);

  Object.entries(params).forEach(([paramKey, paramValue]) => {
    if (paramValue) {
      searchParams.set(paramKey, paramValue);
    }
  });

  const encodedSearchParams = searchParams.toString();
  return encodedSearchParams ? `${pathname}?${encodedSearchParams}` : pathname;
};

const toHashRouterUrl = (url: string) => {
  if (!url) {
    return url;
  }

  if (url.startsWith('/#') || url.startsWith('#')) {
    return url;
  }

  return `/#${url}`;
};

const getSlideCaseLabel = (slideViewerLinkDetails?: SlidePrefetchData) => {
  if (slideViewerLinkDetails?.caseLabel) {
    return slideViewerLinkDetails.caseLabel;
  }

  if (typeof slideViewerLinkDetails?.caseId === 'number') {
    return `Case ${slideViewerLinkDetails.caseId}`;
  }

  return null;
};

const getSlideDisplayLabel = ({
  slideId,
  slideViewerLinkDetails,
}: {
  slideId: string;
  slideViewerLinkDetails?: SlidePrefetchData;
}) => {
  const caseLabel = getSlideCaseLabel(slideViewerLinkDetails);

  return caseLabel ? `${caseLabel} - ${slideId}` : slideId;
};

const getMultichannelRangeFigureHeight = (markerCount: number) => {
  return Math.max(
    minimumOverviewChartHeight,
    Math.max(markerCount, 1) * overviewChartHeightPerMarker + overviewChartHeightPadding
  );
};

const orderMarkerSummaries = (markerSummaries: MarkerNormalizationSummary[], markerIds: string[]) => {
  if (!markerIds.length) {
    return markerSummaries;
  }

  const markerOrder = new Map(markerIds.map((markerId, index) => [markerId, index]));

  return [...markerSummaries].sort((leftSummary, rightSummary) => {
    const leftOrder = markerOrder.get(leftSummary.markerId) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = markerOrder.get(rightSummary.markerId) ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder === rightOrder) {
      return leftSummary.markerId.localeCompare(rightSummary.markerId);
    }

    return leftOrder - rightOrder;
  });
};

const buildSlideMarkerPanelSummaries = (
  normalizationSummary: SlideChannelNormalizationResultSummary[]
): SlideMarkerPanelSummary[] => {
  return compact(
    normalizationSummary.map((slideSummary) => {
      const orderedMarkerIds = uniq(
        compact(
          Object.entries(slideSummary.normalizationsSummary || {})
            .sort(([leftChannelKey], [rightChannelKey]) => Number(leftChannelKey) - Number(rightChannelKey))
            .map(([, normalizationValue]) => {
              const markerId = (normalizationValue as { markerType?: string } | undefined)?.markerType;
              return markerId || null;
            })
        )
      );

      if (!orderedMarkerIds.length) {
        return null;
      }

      const markerIds = sortBy(orderedMarkerIds);

      return {
        slideId: slideSummary.slideId,
        markerIds,
        orderedMarkerIds,
        panelKey: markerIds.join('||'),
        orderKey: orderedMarkerIds.join('||'),
      };
    })
  );
};

const buildCohortPanelGroups = (slideMarkerPanelSummaries: SlideMarkerPanelSummary[]): CohortPanelGroup[] => {
  const groupedPanels = groupBy(slideMarkerPanelSummaries, 'panelKey');

  return orderBy(
    map(groupedPanels, (panelSlides, panelKey) => {
      const orderedPanelSlides = orderBy(panelSlides, ['slideId'], ['asc']);
      const representativePanelSlide = first(orderedPanelSlides);
      const orderVariants = orderBy(
        map(groupBy(orderedPanelSlides, 'orderKey'), (variantSlides, orderKey) => {
          const orderedVariantSlides = orderBy(variantSlides, ['slideId'], ['asc']);

          return {
            orderKey,
            orderedMarkerIds: first(orderedVariantSlides)?.orderedMarkerIds || [],
            slideCount: orderedVariantSlides.length,
            slideIds: map(orderedVariantSlides, 'slideId'),
          };
        }),
        ['slideCount', (variant) => first(variant.slideIds) || ''],
        ['desc', 'asc']
      ).map((variant, variantIndex) => ({
        ...variant,
        variantId: `Order variant ${variantIndex + 1}`,
      }));

      return {
        panelKey,
        markerIds: representativePanelSlide?.markerIds || [],
        representativeOrderMarkerIds: representativePanelSlide?.orderedMarkerIds || [],
        orderVariantCount: uniq(map(orderedPanelSlides, 'orderKey')).length,
        orderVariants,
        slideCount: orderedPanelSlides.length,
        slideIds: map(orderedPanelSlides, 'slideId'),
      };
    }),
    ['slideCount', (panelGroup) => panelGroup.markerIds.length, (panelGroup) => first(panelGroup.slideIds) || ''],
    ['desc', 'desc', 'asc']
  ).map((panelGroup, panelIndex) => ({
    ...panelGroup,
    panelId: `Panel ${panelIndex + 1}`,
  }));
};

const buildPanelDistributionFigure = ({
  cohortPanelGroups,
  getMarkerLabel,
}: {
  cohortPanelGroups: CohortPanelGroup[];
  getMarkerLabel: (markerId: string) => string;
}): PlotlyFigure | null => {
  if (!cohortPanelGroups.length) {
    return null;
  }

  return {
    data: [
      {
        type: 'bar',
        name: 'Slides',
        x: map(cohortPanelGroups, 'panelId'),
        y: map(cohortPanelGroups, 'slideCount'),
        customdata: cohortPanelGroups.map((panelGroup) => [
          panelGroup.markerIds.length,
          panelGroup.orderVariantCount,
          formatMarkerOrder(panelGroup.representativeOrderMarkerIds, getMarkerLabel),
          panelGroup.slideIds.slice(0, 3).join(', '),
        ]),
        text: map(cohortPanelGroups, (panelGroup) => String(panelGroup.slideCount)),
        textposition: 'outside',
        marker: {
          color: 'rgba(25, 118, 210, 0.72)',
        },
        hovertemplate:
          '<b>%{x}</b><br>slides=%{y}<br>markers=%{customdata[0]}<br>order variants=%{customdata[1]}<br>primary order=%{customdata[2]}<br>sample slides=%{customdata[3]}<extra></extra>',
      },
    ],
    layout: {
      margin: { l: 56, r: 24, t: 12, b: 56 },
      meta: {
        themeMode: 'client',
        themeModifiers: {
          axisPadding: { y: 0.12 },
        },
      },
      showlegend: false,
      xaxis: {
        title: { text: 'Panel group' },
      },
      yaxis: {
        title: { text: 'Slides' },
        rangemode: 'tozero',
      },
    },
  };
};
const LoadingMarkerNormalizationInsights: React.FC<{
  markerIds: string[];
  stainTypeIdToDisplayName: (value: string) => string;
}> = ({ markerIds, stainTypeIdToDisplayName }) => {
  const placeholderMarkerIds =
    markerIds.length > 0
      ? markerIds
      : Array.from({ length: minimumLoadingMarkerCount }, (_, index) => `placeholder-${index + 1}`);
  const overviewChartHeight = getMultichannelRangeFigureHeight(placeholderMarkerIds.length);

  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Typography variant="h4" gutterBottom>
        Channel Dynamic Range
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Preparing multichannel normalization insights from the current cohort values.
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" animation="wave" width={228} height={32} sx={{ transform: 'none' }} />
        <Skeleton variant="rounded" animation="wave" width={196} height={32} sx={{ transform: 'none' }} />
        <Skeleton variant="rounded" animation="wave" width={152} height={32} sx={{ transform: 'none' }} />
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={`normalization-stat-${index}`}
            variant="rounded"
            animation="wave"
            width={148}
            height={64}
            sx={{ transform: 'none', borderRadius: 2 }}
          />
        ))}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Multichannel range overview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Preparing the all-marker dynamic range view while normalization values load.
            </Typography>
            <Box sx={{ minHeight: overviewChartHeight, pt: 1 }}>
              {placeholderMarkerIds.map((markerId, index) => {
                const markerLabel = markerId.startsWith('placeholder-')
                  ? `Marker ${index + 1}`
                  : stainTypeIdToDisplayName(markerId) || markerId;

                return (
                  <Box key={markerId} display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ width: 88, flexShrink: 0 }}>
                      {markerLabel}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Skeleton variant="rounded" animation="wave" height={16} sx={{ mb: 0.75, transform: 'none' }} />
                      <Skeleton variant="rounded" animation="wave" width="72%" height={12} sx={{ transform: 'none' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Multichannel z-score outlier map
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Preparing the slide-level drift map while normalization values load.
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
              sx={{ minHeight: 340 }}
            >
              <Skeleton variant="circular" animation="wave" width={180} height={180} />
              <Skeleton variant="text" animation="wave" width="72%" />
              <Skeleton variant="text" animation="wave" width="56%" />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <Box
        data-testid="marker-drill-down-sticky-header"
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={markerDrillDownHeaderSx}
      >
        <Box>
          <Typography variant="subtitle1">Marker drill-down</Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing marker-level drill-down views.
          </Typography>
        </Box>
        <Skeleton variant="rounded" animation="wave" width={280} height={40} sx={{ transform: 'none' }} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rounded" animation="wave" height={300} sx={{ transform: 'none', borderRadius: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rounded" animation="wave" height={300} sx={{ transform: 'none', borderRadius: 2 }} />
        </Grid>
      </Grid>
    </Paper>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <Box
      sx={{
        minWidth: 140,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
};

const QCMarkerNormalizationInsights: React.FC<QCMarkerNormalizationInsightsProps> = ({
  studyId,
  markerIds = [],
  scope = 'study',
}) => {
  const { casesParams } = useCasesParams();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const { generateEncodedParams } = useEncodedFilters();
  const { getUrlToCasePage, getUrlToSlidePage } = useNavigationToViewerPage();
  const queryClient = useQueryClient();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [selectedHistogramSlideId, setSelectedHistogramSlideId] = useState<string | null>(null);
  const [visibleHistogramFlavorNames, setVisibleHistogramFlavorNames] = useState<string[]>([]);
  const [panelDistributionChartEl, setPanelDistributionChartEl] = useState<HTMLElement | null>(null);
  const [multichannelRangeChartEl, setMultichannelRangeChartEl] = useState<HTMLElement | null>(null);
  const [multichannelZScoreChartEl, setMultichannelZScoreChartEl] = useState<HTMLElement | null>(null);
  const [selectedMarkerRangeChartEl, setSelectedMarkerRangeChartEl] = useState<HTMLElement | null>(null);
  const [selectedMarkerZScoreChartEl, setSelectedMarkerZScoreChartEl] = useState<HTMLElement | null>(null);
  const [histogramChartEl, setHistogramChartEl] = useState<HTMLElement | null>(null);
  const knownMarkerIds = useMemo(() => compact(markerIds), [markerIds]);

  const filteredCasesParams = useMemo(
    () => buildScopeCasesParams({ casesParams, scope, studyId }),
    [casesParams, scope, studyId]
  );
  const { data: filteredSlideThumbnailData = [] } = useFilteredCaseSlideThumbnailsData({
    casesParams: filteredCasesParams,
    enabled: Boolean(studyId),
  });

  const { data: normalizationSummary = [], isLoading } = useQuery<SlideChannelNormalizationResultSummary[]>({
    queryKey: ['study-marker-normalization-summary', scope, filteredCasesParams],
    queryFn: ({ signal }) => getNormalizationSummary(filteredCasesParams, signal),
    enabled: Boolean(studyId),
  });
  const slideViewerNavigationPrefetchEncodedParams = useMemo(() => {
    if (!normalizationSummary.length) {
      return null;
    }

    return generateEncodedParams({
      filters: filteredCasesParams.filters,
      clinicalData: filteredCasesParams.clinicalData,
      features: filteredCasesParams.features,
      labId: filteredCasesParams.labId,
      page: 1,
      pageSize: normalizationSummary.length,
      resultsMode: filteredCasesParams.resultsMode,
      slidesMode: filteredCasesParams.slidesMode,
      caseIdsToInclude: filteredCasesParams.caseIdsToInclude,
      slideIdsToInclude: filteredCasesParams.slideIdsToInclude,
    });
  }, [
    filteredCasesParams.caseIdsToInclude,
    filteredCasesParams.clinicalData,
    filteredCasesParams.features,
    filteredCasesParams.filters,
    filteredCasesParams.labId,
    filteredCasesParams.resultsMode,
    filteredCasesParams.slideIdsToInclude,
    filteredCasesParams.slidesMode,
    generateEncodedParams,
    normalizationSummary.length,
  ]);
  const { data: slideViewerNavigationPrefetchData = [] } = useQuery<SlidePrefetchData[]>({
    queryKey: ['study-marker-normalization-viewer-navigation-prefetch', slideViewerNavigationPrefetchEncodedParams],
    queryFn: ({ signal }) =>
      getSlidesViewerNavigationPrefetchData(slideViewerNavigationPrefetchEncodedParams || '', signal),
    enabled: Boolean(slideViewerNavigationPrefetchEncodedParams),
    staleTime: 5 * 60 * 1000,
  });

  const getMarkerLabel = (markerId: string) => stainTypeIdToDisplayName(markerId) || markerId;
  const normalizationInsights = useMemo(
    () => buildMarkerNormalizationInsights(normalizationSummary, knownMarkerIds),
    [knownMarkerIds, normalizationSummary]
  );
  const orderedMarkerSummaries = useMemo(
    () => orderMarkerSummaries(normalizationInsights.markerSummaries, knownMarkerIds),
    [knownMarkerIds, normalizationInsights.markerSummaries]
  );
  const orderedMarkerIds = useMemo(() => map(orderedMarkerSummaries, 'markerId'), [orderedMarkerSummaries]);
  const resolvedSelectedMarkerId = useMemo(() => {
    if (selectedMarkerId && orderedMarkerIds.includes(selectedMarkerId)) {
      return selectedMarkerId;
    }

    return knownMarkerIds.find((markerId) => orderedMarkerIds.includes(markerId)) || orderedMarkerIds[0] || null;
  }, [knownMarkerIds, orderedMarkerIds, selectedMarkerId]);
  const selectedMarkerSummary = useMemo(
    () => find(orderedMarkerSummaries, { markerId: resolvedSelectedMarkerId }) || null,
    [orderedMarkerSummaries, resolvedSelectedMarkerId]
  );
  const selectedObservations = useMemo(
    () =>
      resolvedSelectedMarkerId ? normalizationInsights.observationsByMarkerId[resolvedSelectedMarkerId] || [] : [],
    [normalizationInsights.observationsByMarkerId, resolvedSelectedMarkerId]
  );
  const slideSummaries = normalizationInsights.slideSummaries;
  const normalizationCoverageSummary = useNormalizationCoverageSummary({
    normalizationSummaries: normalizationSummary,
  });
  const slideMarkerIdsBySlideId = useMemo(() => {
    return normalizationSummary.reduce<Record<string, string[]>>((accumulator, slideSummary) => {
      const markerIdsForSlide = Object.values(slideSummary.normalizationsSummary || {}).reduce<string[]>(
        (markerIdsForEntry, normalizationValue) => {
          const markerId = (normalizationValue as { markerType?: string } | undefined)?.markerType;

          if (markerId) {
            markerIdsForEntry.push(markerId);
          }

          return markerIdsForEntry;
        },
        []
      );

      accumulator[slideSummary.slideId] = Array.from(new Set(markerIdsForSlide));
      return accumulator;
    }, {});
  }, [normalizationSummary]);
  const slideViewerLinkDetailsBySlideId = useMemo(() => {
    return slideViewerNavigationPrefetchData.reduce<Record<string, SlidePrefetchData>>((accumulator, slideData) => {
      accumulator[slideData.slideId] = slideData;
      return accumulator;
    }, {});
  }, [slideViewerNavigationPrefetchData]);
  const slideThumbnailDataBySlideId = useMemo(() => {
    return filteredSlideThumbnailData.reduce<Record<string, (typeof filteredSlideThumbnailData)[number]>>(
      (accumulator, slideData) => {
        accumulator[slideData.slideId] = slideData;
        return accumulator;
      },
      {}
    );
  }, [filteredSlideThumbnailData]);
  const getResolvedSlideDisplayLabel = React.useCallback(
    (slideId: string) => {
      const slideViewerLinkDetails =
        slideViewerLinkDetailsBySlideId[slideId] ||
        (typeof slideThumbnailDataBySlideId[slideId]?.caseId === 'number'
          ? ({ caseId: slideThumbnailDataBySlideId[slideId].caseId } as SlidePrefetchData)
          : undefined);

      return getSlideDisplayLabel({
        slideId,
        slideViewerLinkDetails,
      });
    },
    [slideThumbnailDataBySlideId, slideViewerLinkDetailsBySlideId]
  );
  const histogramSlideOptions = useMemo<HistogramSlideOption[]>(
    () =>
      map(
        uniqBy(selectedObservations, (observation) => observation.slideId),
        (observation) => ({
          label: getResolvedSlideDisplayLabel(observation.slideId),
          value: observation.slideId,
          observation,
        })
      ),
    [getResolvedSlideDisplayLabel, selectedObservations]
  );
  const selectedHistogramSlideOption = useMemo(
    () => find(histogramSlideOptions, { value: selectedHistogramSlideId }) || first(histogramSlideOptions) || null,
    [histogramSlideOptions, selectedHistogramSlideId]
  );
  const selectedHistogramObservation = useMemo(
    () => selectedHistogramSlideOption?.observation || null,
    [selectedHistogramSlideOption]
  );
  const slideMarkerPanelSummaries = useMemo(
    () => buildSlideMarkerPanelSummaries(normalizationSummary),
    [normalizationSummary]
  );
  const cohortPanelGroups = useMemo(
    () => buildCohortPanelGroups(slideMarkerPanelSummaries),
    [slideMarkerPanelSummaries]
  );
  const cohortOrderVariantCount = useMemo(
    () => uniq(map(slideMarkerPanelSummaries, 'orderKey')).length,
    [slideMarkerPanelSummaries]
  );
  const dominantPanelGroup = useMemo(() => first(cohortPanelGroups) || null, [cohortPanelGroups]);
  const markerPanelsAlignedAcrossSlides = cohortPanelGroups.length <= 1;
  const markerOrderAlignedAcrossSlides = cohortOrderVariantCount <= 1;
  const shouldRenderPanelDistribution = cohortPanelGroups.length > 1;
  const shouldRenderDetailedCohortStructure = cohortPanelGroups.length > 1 || cohortOrderVariantCount > 1;
  const slideMarkerStructureExportDetailsBySlideId = useMemo(() => {
    return cohortPanelGroups.reduce<Record<string, SlideMarkerStructureExportDetails>>((accumulator, panelGroup) => {
      panelGroup.orderVariants.forEach((orderVariant) => {
        orderVariant.slideIds.forEach((slideId) => {
          accumulator[slideId] = {
            panelId: panelGroup.panelId,
            panelMarkers: panelGroup.markerIds.map((markerId) => getMarkerLabel(markerId)).join(' | '),
            panelSlideCount: panelGroup.slideCount,
            orderVariantCount: panelGroup.orderVariantCount,
            orderVariantId: orderVariant.variantId,
            markerOrder: formatMarkerOrder(orderVariant.orderedMarkerIds, getMarkerLabel),
          };
        });
      });

      return accumulator;
    }, {});
  }, [cohortPanelGroups, stainTypeIdToDisplayName]);
  const cohortMetadataEncodedParams = useMemo(
    () =>
      generateEncodedParams({
        ...filteredCasesParams,
        skipPagination: true,
      }),
    [filteredCasesParams, generateEncodedParams]
  );
  const panelDistributionFigure = useMemo(
    () => buildPanelDistributionFigure({ cohortPanelGroups, getMarkerLabel }),
    [cohortPanelGroups, stainTypeIdToDisplayName]
  );
  const exportCohortMetadataMutation = useMutation({
    mutationKey: ['exportMarkerQcCohortMetadata', studyId, scope, cohortMetadataEncodedParams],
    mutationFn: () =>
      exportProcedureMetadataCsv({
        studyId,
        encodedFilters: cohortMetadataEncodedParams,
        fileNamePrefix: `studyId-${studyId}-${scope}-marker-qc-cohort-metadata`,
        additionalColumns: [
          {
            header: 'markerQc.panelId',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.panelId || '',
          },
          {
            header: 'markerQc.panelMarkers',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.panelMarkers || '',
          },
          {
            header: 'markerQc.panelSlideCount',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.panelSlideCount || '',
          },
          {
            header: 'markerQc.orderVariant',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.orderVariantId || '',
          },
          {
            header: 'markerQc.orderVariantCountInPanel',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.orderVariantCount || '',
          },
          {
            header: 'markerQc.markerOrder',
            getValue: ({ slide }) => slideMarkerStructureExportDetailsBySlideId[slide.id]?.markerOrder || '',
          },
        ],
      }),
  });

  const markerOptions = useMemo<MarkerOption[]>(
    () =>
      map(orderedMarkerSummaries, (summary) => ({
        label: getMarkerLabel(summary.markerId),
        value: summary.markerId,
      })),
    [orderedMarkerSummaries, stainTypeIdToDisplayName]
  );

  const selectedMarkerLabel = selectedMarkerSummary ? getMarkerLabel(selectedMarkerSummary.markerId) : '';

  const multichannelObservations = useMemo(
    () => orderedMarkerIds.flatMap((markerId) => normalizationInsights.observationsByMarkerId[markerId] || []),
    [normalizationInsights.observationsByMarkerId, orderedMarkerIds]
  );
  const multichannelRangeFigure = useMemo(
    () =>
      stripPlotTitle(
        multichannelObservations.length
          ? buildMultichannelRangeOverviewFigure({
              observations: multichannelObservations,
              getMarkerLabel,
            })
          : null
      ),
    [multichannelObservations, stainTypeIdToDisplayName]
  );
  const multichannelZScoreFigure = useMemo(
    () =>
      stripPlotTitle(
        slideSummaries.length
          ? buildMultichannelZScoreScatterFigure({
              slideSummaries,
              getMarkerLabel,
            })
          : null
      ),
    [slideSummaries, stainTypeIdToDisplayName]
  );
  const prefetchedMarkerFigures = useMemo(
    () =>
      buildMarkerNormalizationFigureSets({
        observationsByMarkerId: normalizationInsights.observationsByMarkerId,
        orderedMarkerIds,
        getMarkerLabel,
      }),
    [normalizationInsights.observationsByMarkerId, orderedMarkerIds, stainTypeIdToDisplayName]
  );
  const boxPlotFigure = useMemo(
    () =>
      stripPlotTitle(
        (resolvedSelectedMarkerId && prefetchedMarkerFigures[resolvedSelectedMarkerId]?.rangeFigure) || null
      ),
    [prefetchedMarkerFigures, resolvedSelectedMarkerId]
  );
  const scatterFigure = useMemo(
    () =>
      stripPlotTitle(
        (resolvedSelectedMarkerId && prefetchedMarkerFigures[resolvedSelectedMarkerId]?.zScoreFigure) || null
      ),
    [prefetchedMarkerFigures, resolvedSelectedMarkerId]
  );
  const multichannelRangeFigureHeight = useMemo(
    () => getMultichannelRangeFigureHeight(orderedMarkerSummaries.length),
    [orderedMarkerSummaries.length]
  );

  const cohortOutliers = useMemo(
    () => compact(map(selectedObservations, (observation) => (observation.isOutlier ? observation.slideId : null))),
    [selectedObservations]
  );

  const slidesWithOutlierDimensions = useMemo(
    () => slideSummaries.filter((slideSummary) => slideSummary.outlierMarkerCount > 0),
    [slideSummaries]
  );
  const slideObservationsBySlideId = useMemo(() => {
    return multichannelObservations.reduce<Record<string, MarkerNormalizationObservation[]>>(
      (accumulator, observation) => {
        if (!accumulator[observation.slideId]) {
          accumulator[observation.slideId] = [];
        }

        accumulator[observation.slideId].push(observation);
        return accumulator;
      },
      {}
    );
  }, [multichannelObservations]);
  const getRelevantViewerMarkerIdsForSlide = React.useCallback(
    (slideId: string, markerIdsToShow: string[]) => {
      const dnaMarkerIds = (slideMarkerIdsBySlideId[slideId] || []).filter(isDnaMarkerId);
      return uniq(compact([...markerIdsToShow, ...dnaMarkerIds]));
    },
    [slideMarkerIdsBySlideId]
  );
  const getOutlierMarkerIdsForSlide = React.useCallback(
    (slideId: string) => {
      return Array.from(
        new Set(
          (slideObservationsBySlideId[slideId] || [])
            .filter((observation) => observation.isOutlier)
            .map((observation) => observation.markerId)
        )
      );
    },
    [slideObservationsBySlideId]
  );
  const getSlideSummaryMarkerIds = React.useCallback(
    (slideSummary: SlideNormalizationSummary) => {
      return uniq(
        compact([
          ...getOutlierMarkerIdsForSlide(slideSummary.slideId),
          slideSummary.dominantMinMarkerId,
          slideSummary.dominantMaxMarkerId,
          slideSummary.dominantRangeMarkerId,
        ])
      );
    },
    [getOutlierMarkerIdsForSlide]
  );
  const getCaseIdForSlide = React.useCallback(
    (slideId: string) => {
      return slideViewerLinkDetailsBySlideId[slideId]?.caseId ?? slideThumbnailDataBySlideId[slideId]?.caseId;
    },
    [slideThumbnailDataBySlideId, slideViewerLinkDetailsBySlideId]
  );
  const buildCaseViewUrlForSlide = React.useCallback(
    ({ slideId, markerIdsToShow }: { slideId: string; markerIdsToShow: string[] }) => {
      const slideViewerLinkDetails = slideViewerLinkDetailsBySlideId[slideId];
      const slideThumbnailData = slideThumbnailDataBySlideId[slideId];
      const caseId = getCaseIdForSlide(slideId);
      const viewerMarkerIds = getRelevantViewerMarkerIdsForSlide(slideId, markerIdsToShow);
      const toggleMarkerIds = uniq([...orderedMarkerIds, ...(slideMarkerIdsBySlideId[slideId] || [])]);
      const markerChannelToggle = encodeMarkerChannelToggle({
        visibleMarkerIds: viewerMarkerIds,
        toggleMarkerIds,
      });
      const markerChannelHistograms = encodeMarkerChannelHistograms({
        markerTypes: markerIdsToShow,
        viewerIndex: multiplexViewerIndex,
      });
      const baseViewerUrl =
        typeof caseId === 'number'
          ? getUrlToCasePage({
              caseId,
              caseStudyId: studyId,
              labId: slideViewerLinkDetails?.labId || slideThumbnailData?.labId || filteredCasesParams.labId,
              selectedSlideIds: [slideId],
              viewersStainPreferences: slideViewerLinkDetails?.stainTypeId
                ? [slideViewerLinkDetails.stainTypeId]
                : undefined,
            })
          : getUrlToSlidePage({
              slideId,
              labId: slideViewerLinkDetails?.labId || slideThumbnailData?.labId || filteredCasesParams.labId,
              caseStudyId: studyId,
            });

      return toHashRouterUrl(
        addSearchParamsToUrl(baseViewerUrl, {
          markerChannelToggle,
          [markerChannelHistogramsQueryParam]: markerChannelHistograms,
        })
      );
    },
    [
      filteredCasesParams.labId,
      getCaseIdForSlide,
      getRelevantViewerMarkerIdsForSlide,
      getUrlToCasePage,
      getUrlToSlidePage,
      orderedMarkerIds,
      slideMarkerIdsBySlideId,
      slideThumbnailDataBySlideId,
      slideViewerLinkDetailsBySlideId,
      studyId,
    ]
  );
  const renderSlidePreviewCell = React.useCallback(
    ({ slideId, markerIdsToShow }: { slideId: string; markerIdsToShow: string[] }) => {
      const slideViewerLinkDetails = slideViewerLinkDetailsBySlideId[slideId];
      const slideThumbnailData = slideThumbnailDataBySlideId[slideId];
      const caseId = getCaseIdForSlide(slideId);
      const slideDisplayLabel = getResolvedSlideDisplayLabel(slideId);
      const slideLabId = slideViewerLinkDetails?.labId || slideThumbnailData?.labId || filteredCasesParams.labId;
      const slide = {
        ...slideViewerLinkDetails,
        id: slideId,
        labId: slideLabId,
        originalFileName: slideViewerLinkDetails?.originalFileName,
        stainingType: slideViewerLinkDetails?.stainTypeId,
        channelMarkerTypes: slideViewerLinkDetails?.channelMarkerTypes,
      } as Slide;
      const slideUrl = buildCaseViewUrlForSlide({ slideId, markerIdsToShow });

      return (
        <SlidePreviewCell
          slides={[slide]}
          caseId={typeof caseId === 'number' ? caseId : undefined}
          caseStudyId={studyId}
          labId={slideLabId}
          summaryContent={
            <Link
              href={slideUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="secondary"
              sx={{
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {slideDisplayLabel}
            </Link>
          }
          openViewerLink={{
            href: slideUrl,
            label: typeof caseId === 'number' ? 'Open case in viewer' : 'Open slide in viewer',
            target: '_blank',
            rel: 'noopener noreferrer',
          }}
        />
      );
    },
    [
      buildCaseViewUrlForSlide,
      filteredCasesParams.labId,
      getCaseIdForSlide,
      getResolvedSlideDisplayLabel,
      slideThumbnailDataBySlideId,
      slideViewerLinkDetailsBySlideId,
      studyId,
    ]
  );
  const openCaseViewForSlide = React.useCallback(
    ({ slideId, markerIdsToShow }: { slideId: string; markerIdsToShow: string[] }) => {
      if (typeof window === 'undefined') {
        return;
      }

      const viewerUrl = buildCaseViewUrlForSlide({ slideId, markerIdsToShow });
      window.open(viewerUrl, '_blank', 'noopener,noreferrer');
    },
    [buildCaseViewUrlForSlide]
  );
  const multichannelOutlierCandidates = useMemo<MultichannelOutlierCandidate[]>(() => {
    return slidesWithOutlierDimensions.reduce<MultichannelOutlierCandidate[]>((accumulator, slideSummary) => {
      const outlierMarkerIds = getOutlierMarkerIdsForSlide(slideSummary.slideId);

      if (!outlierMarkerIds.length) {
        return accumulator;
      }

      accumulator.push({
        slideId: slideSummary.slideId,
        outlierMarkerIds,
        outlierMarkerCount: slideSummary.outlierMarkerCount,
        maxAbsoluteZScore: slideSummary.maxAbsoluteZScore,
      });

      return accumulator;
    }, []);
  }, [getOutlierMarkerIdsForSlide, slidesWithOutlierDimensions]);

  const maxObservedAbsoluteZScore = useMemo(() => max(map(slideSummaries, 'maxAbsoluteZScore')) || 0, [slideSummaries]);

  useEffect(() => {
    setSelectedHistogramSlideId(null);
  }, [resolvedSelectedMarkerId]);

  const selectedHistogramSlideUrl = useMemo(() => {
    if (!selectedHistogramObservation) {
      return null;
    }

    return buildCaseViewUrlForSlide({
      slideId: selectedHistogramObservation.slideId,
      markerIdsToShow: [selectedHistogramObservation.markerId],
    });
  }, [buildCaseViewUrlForSlide, selectedHistogramObservation]);
  const selectedHistogramSlideDisplayLabel = useMemo(() => {
    if (!selectedHistogramObservation) {
      return null;
    }

    return getResolvedSlideDisplayLabel(selectedHistogramObservation.slideId);
  }, [getResolvedSlideDisplayLabel, selectedHistogramObservation]);
  const multichannelRangePointLinkConfig = useMemo(
    () => getPointLinkConfig(multichannelRangeFigure),
    [multichannelRangeFigure]
  );
  const multichannelZScorePointLinkConfig = useMemo(
    () => getPointLinkConfig(multichannelZScoreFigure),
    [multichannelZScoreFigure]
  );
  const boxPlotPointLinkConfig = useMemo(() => getPointLinkConfig(boxPlotFigure), [boxPlotFigure]);
  const scatterPointLinkConfig = useMemo(() => getPointLinkConfig(scatterFigure), [scatterFigure]);
  const handleMultichannelRangePointClick = React.useCallback(
    (event: unknown) => {
      const customdata = getPlotlyPointCustomdata(event);
      const slideId = getPlotlyCustomdataString(customdata, multichannelRangePointLinkConfig?.customdataIndex);
      const markerId = getPlotlyCustomdataString(customdata, multichannelRangePointLinkConfig?.markerIdCustomdataIndex);

      if (!slideId) {
        return;
      }

      openCaseViewForSlide({
        slideId,
        markerIdsToShow: markerId ? [markerId] : [],
      });
    },
    [multichannelRangePointLinkConfig, openCaseViewForSlide]
  );
  const handleMultichannelZScorePointClick = React.useCallback(
    (event: unknown) => {
      const customdata = getPlotlyPointCustomdata(event);
      const slideId = getPlotlyCustomdataString(customdata, multichannelZScorePointLinkConfig?.customdataIndex);

      if (!slideId) {
        return;
      }

      openCaseViewForSlide({
        slideId,
        markerIdsToShow: getOutlierMarkerIdsForSlide(slideId),
      });
    },
    [getOutlierMarkerIdsForSlide, multichannelZScorePointLinkConfig, openCaseViewForSlide]
  );
  const handleSelectedMarkerPointClick = React.useCallback(
    (event: unknown, pointLinkConfig: PlotlyPointLinkConfig | null) => {
      const customdata = getPlotlyPointCustomdata(event);
      const slideId = getPlotlyCustomdataString(customdata, pointLinkConfig?.customdataIndex);

      if (!slideId || !resolvedSelectedMarkerId) {
        return;
      }

      openCaseViewForSlide({
        slideId,
        markerIdsToShow: [resolvedSelectedMarkerId],
      });
    },
    [openCaseViewForSlide, resolvedSelectedMarkerId]
  );

  const { data: histogramPreviewResponse, isLoading: isLoadingHistogramPreview } =
    useQuery<SlideChannelHistogramPreviewResponse>({
      queryKey: selectedHistogramObservation
        ? getSlideChannelHistogramPreviewQueryKey(getHistogramPreviewRequestParams(selectedHistogramObservation))
        : ['slideChannelHistogramPreview', 'idle'],
      queryFn: ({ signal }) =>
        getSlideChannelHistogramPreview(getHistogramPreviewRequestParams(selectedHistogramObservation!), signal),
      enabled: Boolean(selectedHistogramObservation),
      retry: false,
      staleTime: histogramPreviewStaleTimeMs,
    });

  const selectedHistogramBitDepthLabel = useMemo(
    () => getHistogramBitDepthLabel(histogramPreviewResponse?.histogramPreview),
    [histogramPreviewResponse?.histogramPreview]
  );
  const histogramFlavorOptions = useMemo<HistogramFlavorOption[]>(
    () =>
      histogramPreviewResponse?.histogramPreview
        ? getHistogramFlavorEntries(histogramPreviewResponse.histogramPreview).map(([flavor], flavorIndex) => ({
            flavor,
            label: getHistogramFlavorLabel(flavor),
            color: getHistogramFlavorColor(flavor, flavorIndex),
          }))
        : [],
    [histogramPreviewResponse?.histogramPreview]
  );
  const availableHistogramFlavorNames = useMemo(
    () => map(histogramFlavorOptions, (flavorOption) => flavorOption.flavor),
    [histogramFlavorOptions]
  );
  const selectedHistogramTotalPixelsLabel = useMemo(
    () => getHistogramTotalPixelsLabel(histogramPreviewResponse?.histogramPreview, visibleHistogramFlavorNames),
    [histogramPreviewResponse?.histogramPreview, visibleHistogramFlavorNames]
  );
  const histogramExportFileName = useMemo(
    () => getHistogramExportFileName({ markerLabel: selectedMarkerLabel, observation: selectedHistogramObservation }),
    [selectedHistogramObservation, selectedMarkerLabel]
  );

  useEffect(() => {
    setVisibleHistogramFlavorNames((currentFlavorNames) => {
      if (!availableHistogramFlavorNames.length) {
        return currentFlavorNames.length ? [] : currentFlavorNames;
      }

      const visibleAvailableFlavorNames = currentFlavorNames.filter((flavorName) =>
        availableHistogramFlavorNames.includes(flavorName)
      );
      const nextFlavorNames = visibleAvailableFlavorNames.length
        ? visibleAvailableFlavorNames
        : availableHistogramFlavorNames;

      if (
        nextFlavorNames.length === currentFlavorNames.length &&
        nextFlavorNames.every((flavorName, flavorIndex) => flavorName === currentFlavorNames[flavorIndex])
      ) {
        return currentFlavorNames;
      }

      return nextFlavorNames;
    });
  }, [availableHistogramFlavorNames]);

  const handleHistogramFlavorToggle = React.useCallback(
    (flavor: string) => {
      setVisibleHistogramFlavorNames((currentFlavorNames) => {
        if (currentFlavorNames.includes(flavor)) {
          if (currentFlavorNames.length <= 1) {
            return currentFlavorNames;
          }

          return currentFlavorNames.filter((currentFlavorName) => currentFlavorName !== flavor);
        }

        return availableHistogramFlavorNames.filter(
          (availableFlavorName) => currentFlavorNames.includes(availableFlavorName) || availableFlavorName === flavor
        );
      });
    },
    [availableHistogramFlavorNames]
  );

  useEffect(() => {
    if (!selectedHistogramObservation || histogramSlideOptions.length <= 1) {
      return undefined;
    }

    return scheduleIdleWork(() => {
      histogramSlideOptions
        .filter((histogramSlideOption) => histogramSlideOption.value !== selectedHistogramObservation.slideId)
        .slice(0, idleHistogramPrefetchLimit)
        .forEach((histogramSlideOption) => {
          const requestParams = getHistogramPreviewRequestParams(histogramSlideOption.observation);
          const queryKey = getSlideChannelHistogramPreviewQueryKey(requestParams);

          if (queryClient.getQueryData(queryKey)) {
            return;
          }

          queryClient.prefetchQuery({
            queryKey,
            queryFn: ({ signal }) => getSlideChannelHistogramPreview(requestParams, signal),
            staleTime: histogramPreviewStaleTimeMs,
          });
        });
    });
  }, [histogramSlideOptions, queryClient, selectedHistogramObservation]);

  const histogramPreviewFigure = useMemo(
    () =>
      stripPlotTitle(
        histogramPreviewResponse?.histogramPreview
          ? buildChannelHistogramPreviewFigure({
              histogramPreview: histogramPreviewResponse.histogramPreview,
              markerLabel: selectedMarkerLabel,
              visibleFlavorNames: visibleHistogramFlavorNames,
            })
          : null
      ),
    [histogramPreviewResponse?.histogramPreview, selectedMarkerLabel, visibleHistogramFlavorNames]
  );

  if (isLoading) {
    return (
      <LoadingMarkerNormalizationInsights
        markerIds={knownMarkerIds}
        stainTypeIdToDisplayName={stainTypeIdToDisplayName}
      />
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Typography variant="h4" gutterBottom>
        Channel Dynamic Range
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Review the current normalization min/max values for each marker across the selected cohort scope. Start with the
        cohort marker structure to confirm which slides are directly comparable, then use the drift charts to spot
        outliers and use the chart action menus for fullscreen review or exports when you need to share findings.
      </Typography>

      {!orderedMarkerSummaries.length || !selectedMarkerSummary ? (
        <Typography color="text.secondary">
          {scope === 'study'
            ? 'No current channel normalization values were found for this study slide set.'
            : 'No current channel normalization values were found for the filtered dataset scope.'}
        </Typography>
      ) : (
        <>
          <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip label={`Markers with current normalization: ${orderedMarkerSummaries.length}`} />
              <Chip label={`Slides with normalization: ${slideSummaries.length}`} />
              <Chip label={`Scope: ${scope === 'study' ? 'Entire study' : 'Dataset filters'}`} />
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
            <StatCard label="Slides with any outlier" value={String(slidesWithOutlierDimensions.length)} />
            <StatCard label="Highest |z|" value={formatMetric(maxObservedAbsoluteZScore)} />
            <StatCard label="Current lower bound" value={formatMetric(selectedMarkerSummary.minCurrentValue)} />
            <StatCard label="Current upper bound" value={formatMetric(selectedMarkerSummary.maxCurrentValue)} />
            <StatCard
              label="Middle 50% range"
              value={formatMetricBand(
                selectedMarkerSummary.dynamicRangeLowerQuartile,
                selectedMarkerSummary.dynamicRangeUpperQuartile
              )}
            />
          </Box>

          <NormalizationCoverageSummaryPanel
            coverageSummary={normalizationCoverageSummary}
            getMarkerLabel={getMarkerLabel}
            renderSlideCell={renderSlidePreviewCell}
            allAvailableMessage={
              cohortPanelGroups.length === 1 && cohortPanelGroups[0]
                ? `All ${cohortPanelGroups[0].slideCount} slides with ${cohortPanelGroups[0].markerIds.length} channels have normalization values (dynamic ranges are set).`
                : undefined
            }
          />

          <Box sx={{ mb: 2 }}>
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={2}
              sx={{ mb: 1.5 }}
            >
              <Box sx={{ minWidth: 0, flex: '1 1 360px' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Cohort marker structure
                </Typography>
                <Typography color="text.secondary">
                  Confirm whether slides share the same marker panel and marker order before comparing outliers. Panel
                  groups below are based on the markers present on each slide, while order variants show whether channel
                  order changes within the same panel.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => exportCohortMetadataMutation.mutate()}
                disabled={exportCohortMetadataMutation.isLoading}
                startIcon={
                  exportCohortMetadataMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
              >
                Export cohort metadata CSV
              </Button>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
              <StatCard label="Distinct panels" value={String(cohortPanelGroups.length)} />
              <StatCard label="Order variants" value={String(cohortOrderVariantCount)} />
              <StatCard label="Marker panel match" value={markerPanelsAlignedAcrossSlides ? 'Aligned' : 'Mixed'} />
              <StatCard label="Marker order" value={markerOrderAlignedAcrossSlides ? 'Consistent' : 'Mixed'} />
              <StatCard
                label="Dominant panel coverage"
                value={`${dominantPanelGroup?.slideCount || 0} / ${slideSummaries.length}`}
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {shouldRenderPanelDistribution ? (
                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Panel groups across the cohort
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Each bar shows how many slides share the same marker panel. Use the chart actions for
                          fullscreen review or exports.
                        </Typography>
                      </Box>
                      {panelDistributionFigure ? (
                        <PlotlyChartActions
                          chartEl={panelDistributionChartEl}
                          figure={panelDistributionFigure}
                          title="Panel groups across the cohort"
                        />
                      ) : null}
                    </Box>
                    {panelDistributionFigure ? (
                      <ThemedPlotlyFigure
                        figure={panelDistributionFigure}
                        height={280}
                        onPlotlyInit={setPanelDistributionChartEl}
                      />
                    ) : null}
                  </Paper>
                </Grid>
              ) : null}
              <Grid item xs={12} md={shouldRenderPanelDistribution ? 7 : 12}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {shouldRenderDetailedCohortStructure ? 'Panel order variants' : 'Panel order status'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {shouldRenderDetailedCohortStructure
                      ? 'Each panel is broken into explicit order variants so a single cohort panel with mixed channel order does not collapse into one unreadable sequence.'
                      : 'All slides currently share one marker panel and one channel order, so the cohort is ready for direct comparison without a slide-by-slide checklist.'}
                  </Typography>
                  {shouldRenderDetailedCohortStructure ? (
                    <Stack spacing={1.5}>
                      {map(cohortPanelGroups, (panelGroup) => {
                        const referenceOrder = panelGroup.orderVariants[0]?.orderedMarkerIds ?? [];
                        return (
                          <Box
                            key={panelGroup.panelKey}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
                              <Chip size="small" label={panelGroup.panelId} />
                              <Chip size="small" label={`${panelGroup.slideCount} slides`} variant="outlined" />
                              <Chip size="small" label={`${panelGroup.markerIds.length} markers`} variant="outlined" />
                              <Chip
                                size="small"
                                label={`${panelGroup.orderVariantCount} order variants`}
                                variant="outlined"
                              />
                            </Box>

                            {panelGroup.orderVariantCount > 1 && (
                              <Box display="flex" gap={2} sx={{ mb: 1 }}>
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    No dot = matches reference
                                  </Typography>
                                </Box>
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'error.main',
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Shifted from reference
                                  </Typography>
                                </Box>
                              </Box>
                            )}

                            <Stack spacing={1}>
                              {map(panelGroup.orderVariants, (orderVariant, variantIndex) => (
                                <Box
                                  key={orderVariant.orderKey}
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    bgcolor: 'action.hover',
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      label={
                                        variantIndex === 0
                                          ? `${orderVariant.variantId} (reference)`
                                          : orderVariant.variantId
                                      }
                                    />
                                    <Chip size="small" variant="outlined" label={`${orderVariant.slideCount} slides`} />
                                  </Box>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                  >
                                    Marker order
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" gap={0.75} sx={{ mb: 1.25 }}>
                                    {map(orderVariant.orderedMarkerIds, (markerId, markerIndex) => {
                                      const isShifted = variantIndex > 0 && markerId !== referenceOrder[markerIndex];
                                      return (
                                        <Chip
                                          key={`${orderVariant.orderKey}-${markerId}-${markerIndex}`}
                                          size="small"
                                          label={buildMarkerChipLabel(
                                            `${markerIndex + 1}. ${getMarkerLabel(markerId)}`,
                                            isShifted ? 'error.main' : undefined
                                          )}
                                          variant="outlined"
                                        />
                                      );
                                    })}
                                  </Box>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                  >
                                    Slides in this order
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" gap={0.75}>
                                    {map(orderVariant.slideIds, (slideId) => (
                                      <Link
                                        key={`${orderVariant.orderKey}-${slideId}`}
                                        href={buildCaseViewUrlForSlide({
                                          slideId,
                                          markerIdsToShow: orderVariant.orderedMarkerIds,
                                        })}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="none"
                                        color="inherit"
                                        sx={{ display: 'inline-flex' }}
                                      >
                                        <Chip
                                          size="small"
                                          clickable
                                          label={getResolvedSlideDisplayLabel(slideId)}
                                          variant="outlined"
                                        />
                                      </Link>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : dominantPanelGroup ? (
                    <Box>
                      <Alert severity="success" variant="outlined" sx={{ mb: 1.5 }}>
                        All {slideSummaries.length} slides share the same marker panel and channel order.
                      </Alert>
                      <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
                        <Chip size="small" color="success" label={dominantPanelGroup.panelId} />
                        <Chip size="small" label={`${dominantPanelGroup.slideCount} slides`} variant="outlined" />
                        <Chip
                          size="small"
                          label={`${dominantPanelGroup.markerIds.length} markers`}
                          variant="outlined"
                        />
                        <Chip size="small" label="Single shared order" variant="outlined" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Shared marker order
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.75}>
                        {map(dominantPanelGroup.representativeOrderMarkerIds, (markerId, markerIndex) => (
                          <Chip
                            key={`${dominantPanelGroup.panelKey}-${markerId}-${markerIndex}`}
                            size="small"
                            label={`${markerIndex + 1}. ${getMarkerLabel(markerId)}`}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {multichannelOutlierCandidates.length ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Multichannel outlier candidate review
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                Open a slide to inspect the outlier markers in context. The viewer still receives the relevant channel
                policy, but this table stays focused on the outlier markers and their drift severity.
              </Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <TableContainer
                  sx={{
                    maxHeight: 260,
                    ...markerQcTableStickyHeaderSx,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Slide</TableCell>
                        <TableCell>Outlier markers</TableCell>
                        <TableCell align="right">Outlier count</TableCell>
                        <TableCell align="right">Max |z|</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {multichannelOutlierCandidates.map((candidate) => (
                        <TableRow key={candidate.slideId} hover>
                          <TableCell>
                            {renderSlidePreviewCell({
                              slideId: candidate.slideId,
                              markerIdsToShow: candidate.outlierMarkerIds,
                            })}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {candidate.outlierMarkerIds.map((markerId) => (
                                <Chip
                                  key={`${candidate.slideId}-${markerId}`}
                                  size="small"
                                  label={getMarkerLabel(markerId)}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{candidate.outlierMarkerCount}</TableCell>
                          <TableCell align="right">{formatMetric(candidate.maxAbsoluteZScore)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No multichannel outlier candidates were detected in the selected cohort scope.
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Multichannel range overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Horizontal box plots for lower bound, upper bound, and dynamic range across all markers at once.
                    </Typography>
                  </Box>
                  {multichannelRangeFigure ? (
                    <PlotlyChartActions
                      chartEl={multichannelRangeChartEl}
                      figure={multichannelRangeFigure}
                      title="Multichannel range overview"
                    />
                  ) : null}
                </Box>
                {multichannelRangeFigure ? (
                  <ThemedPlotlyFigure
                    figure={multichannelRangeFigure}
                    height={multichannelRangeFigureHeight}
                    onPlotlyInit={setMultichannelRangeChartEl}
                    onPointClick={multichannelRangePointLinkConfig ? handleMultichannelRangePointClick : undefined}
                  />
                ) : null}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Multichannel z-score outlier map
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each point is a slide. Axes show the strongest lower-bound and upper-bound drift across markers,
                      and bubble size shows the strongest range drift.
                    </Typography>
                  </Box>
                  {multichannelZScoreFigure ? (
                    <PlotlyChartActions
                      chartEl={multichannelZScoreChartEl}
                      figure={multichannelZScoreFigure}
                      title="Multichannel z-score outlier map"
                    />
                  ) : null}
                </Box>
                {multichannelZScoreFigure ? (
                  <ThemedPlotlyFigure
                    figure={multichannelZScoreFigure}
                    height={340}
                    onPlotlyInit={setMultichannelZScoreChartEl}
                    onPointClick={multichannelZScorePointLinkConfig ? handleMultichannelZScorePointClick : undefined}
                  />
                ) : null}
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
            <TableContainer
              sx={{
                maxHeight: 260,
                ...markerQcTableStickyHeaderSx,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slide</TableCell>
                    <TableCell align="right">Markers</TableCell>
                    <TableCell align="right">Outlier count</TableCell>
                    <TableCell>Strongest low drift</TableCell>
                    <TableCell>Strongest high drift</TableCell>
                    <TableCell>Strongest range drift</TableCell>
                    <TableCell align="right">Max |z|</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(slideSummaries, (slideSummary: SlideNormalizationSummary) => (
                    <TableRow key={slideSummary.slideId} hover>
                      <TableCell>
                        {renderSlidePreviewCell({
                          slideId: slideSummary.slideId,
                          markerIdsToShow: getSlideSummaryMarkerIds(slideSummary),
                        })}
                      </TableCell>
                      <TableCell align="right">{slideSummary.markerCount}</TableCell>
                      <TableCell align="right">{slideSummary.outlierMarkerCount}</TableCell>
                      <TableCell>
                        {getMarkerLabel(slideSummary.dominantMinMarkerId)} (
                        {formatMetric(slideSummary.dominantMinZScore)})
                      </TableCell>
                      <TableCell>
                        {getMarkerLabel(slideSummary.dominantMaxMarkerId)} (
                        {formatMetric(slideSummary.dominantMaxZScore)})
                      </TableCell>
                      <TableCell>
                        {getMarkerLabel(slideSummary.dominantRangeMarkerId)} (
                        {formatMetric(slideSummary.dominantRangeZScore)})
                      </TableCell>
                      <TableCell align="right">{formatMetric(slideSummary.maxAbsoluteZScore)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Divider sx={{ mb: 2 }} />

          <Box
            data-testid="marker-drill-down-sticky-header"
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={markerDrillDownHeaderSx}
          >
            <Box>
              <Typography variant="subtitle1">Marker drill-down</Typography>
              <Typography variant="body2" color="text.secondary">
                Use the selector below when you want to inspect one marker after finding a suspicious multichannel
                slide.
              </Typography>
            </Box>
            <Autocomplete
              disableClearable
              options={markerOptions}
              value={find(markerOptions, { value: resolvedSelectedMarkerId }) || null}
              onChange={(_, nextValue) => {
                setSelectedHistogramSlideId(null);
                setSelectedMarkerId(nextValue?.value || null);
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} label="Marker" />}
              size="small"
              sx={{ minWidth: 280, maxWidth: 420 }}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
            <StatCard label="Selected marker slides" value={String(selectedMarkerSummary.slideCount)} />
            <StatCard label="Selected marker outliers" value={String(selectedMarkerSummary.outlierCount)} />
            <StatCard label="Range median" value={formatMetric(selectedMarkerSummary.dynamicRangeMedian)} />
            <StatCard label="Range IQR" value={formatMetric(selectedMarkerSummary.dynamicRangeIqr)} />
            <StatCard
              label="Outlier band"
              value={formatMetricBand(selectedMarkerSummary.lowerFence, selectedMarkerSummary.upperFence)}
            />
          </Box>

          {cohortOutliers.length ? (
            <Box sx={{ mb: 2 }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Potential outliers for {selectedMarkerLabel}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.75}>
                {map(cohortOutliers, (slideId) => (
                  <Link
                    key={`selected-marker-outlier-${slideId}`}
                    href={buildCaseViewUrlForSlide({
                      slideId,
                      markerIdsToShow: resolvedSelectedMarkerId ? [resolvedSelectedMarkerId] : [],
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    color="inherit"
                    sx={{ display: 'inline-flex' }}
                  >
                    <Chip
                      size="small"
                      clickable
                      color="warning"
                      variant="outlined"
                      label={getResolvedSlideDisplayLabel(slideId)}
                    />
                  </Link>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No dynamic-range outliers were detected for {selectedMarkerLabel}.
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Selected marker range box plot
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Box plot plus per-slide points for {selectedMarkerLabel}.
                    </Typography>
                  </Box>
                  {boxPlotFigure ? (
                    <PlotlyChartActions
                      chartEl={selectedMarkerRangeChartEl}
                      figure={boxPlotFigure}
                      title={`${selectedMarkerLabel} range box plot`}
                    />
                  ) : null}
                </Box>
                {boxPlotFigure ? (
                  <ThemedPlotlyFigure
                    figure={boxPlotFigure}
                    height={300}
                    onPlotlyInit={setSelectedMarkerRangeChartEl}
                    onPointClick={
                      boxPlotPointLinkConfig
                        ? (event) => handleSelectedMarkerPointClick(event, boxPlotPointLinkConfig)
                        : undefined
                    }
                  />
                ) : null}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Selected marker z-score scatter
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each point is a slide. Dotted guide lines at ±2 standard deviations help isolate drift.
                    </Typography>
                  </Box>
                  {scatterFigure ? (
                    <PlotlyChartActions
                      chartEl={selectedMarkerZScoreChartEl}
                      figure={scatterFigure}
                      title={`${selectedMarkerLabel} z-score scatter`}
                    />
                  ) : null}
                </Box>
                {scatterFigure ? (
                  <ThemedPlotlyFigure
                    figure={scatterFigure}
                    height={300}
                    onPlotlyInit={setSelectedMarkerZScoreChartEl}
                    onPointClick={
                      scatterPointLinkConfig
                        ? (event) => handleSelectedMarkerPointClick(event, scatterPointLinkConfig)
                        : undefined
                    }
                  />
                ) : null}
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer
              sx={{
                maxHeight: 340,
                ...markerQcTableStickyHeaderSx,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slide</TableCell>
                    <TableCell align="right">min_c</TableCell>
                    <TableCell align="right">max_c</TableCell>
                    <TableCell align="right">Range</TableCell>
                    <TableCell align="right">Min z</TableCell>
                    <TableCell align="right">Max z</TableCell>
                    <TableCell align="right">Range z</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(selectedObservations, (observation: MarkerNormalizationObservation) => (
                    <TableRow
                      key={`${observation.slideId}-${observation.channelKey}`}
                      hover
                      sx={{
                        backgroundColor: observation.isOutlier ? 'action.hover' : 'inherit',
                      }}
                    >
                      <TableCell>
                        {renderSlidePreviewCell({
                          slideId: observation.slideId,
                          markerIdsToShow: [observation.markerId],
                        })}
                      </TableCell>
                      <TableCell align="right">{formatMetric(observation.minValue)}</TableCell>
                      <TableCell align="right">{formatMetric(observation.maxValue)}</TableCell>
                      <TableCell align="right">{formatMetric(observation.dynamicRange)}</TableCell>
                      <TableCell align="right">{formatMetric(observation.minZScore)}</TableCell>
                      <TableCell align="right">{formatMetric(observation.maxZScore)}</TableCell>
                      <TableCell align="right">{formatMetric(observation.rangeZScore)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={observation.isOutlier ? 'Outlier' : 'Within range'}
                          color={observation.isOutlier ? 'warning' : 'default'}
                          variant={observation.isOutlier ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {selectedHistogramObservation ? (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="subtitle1">Selected channel histogram</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a slide to inspect for the selected marker. By default, this starts from the first slide in
                    the current cohort order for consistent comparisons.
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                  <Autocomplete
                    disableClearable
                    options={histogramSlideOptions}
                    value={selectedHistogramSlideOption}
                    onChange={(_, nextValue) => {
                      setSelectedHistogramSlideId(nextValue?.value || null);
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => <TextField {...params} label="Histogram slide" />}
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                  {selectedHistogramSlideUrl ? (
                    <Link
                      href={selectedHistogramSlideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="secondary"
                    >
                      {selectedHistogramSlideDisplayLabel
                        ? `Open ${selectedHistogramSlideDisplayLabel} in case view`
                        : 'Open selected case view'}
                    </Link>
                  ) : null}
                  {histogramPreviewFigure ? (
                    <PlotlyChartActions
                      chartEl={histogramChartEl}
                      figure={histogramPreviewFigure}
                      fileName={histogramExportFileName}
                      title={
                        selectedHistogramSlideDisplayLabel
                          ? `${selectedMarkerLabel} histogram - ${selectedHistogramSlideDisplayLabel}`
                          : `${selectedMarkerLabel} histogram`
                      }
                    />
                  ) : null}
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {selectedHistogramSlideUrl ? (
                  <Link
                    href={selectedHistogramSlideUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    color="inherit"
                    sx={{ display: 'inline-flex' }}
                  >
                    <Chip
                      size="small"
                      clickable
                      label={`Slide: ${selectedHistogramSlideDisplayLabel || selectedHistogramObservation.slideId}`}
                    />
                  </Link>
                ) : (
                  <Chip
                    size="small"
                    label={`Slide: ${selectedHistogramSlideDisplayLabel || selectedHistogramObservation.slideId}`}
                  />
                )}
                <Chip size="small" label={`Channel key: ${selectedHistogramObservation.channelKey}`} />
                {histogramPreviewResponse?.histogramPreview?.resultType ? (
                  <Chip size="small" label={`Artifact: ${histogramPreviewResponse.histogramPreview.resultType}`} />
                ) : null}
                {selectedHistogramBitDepthLabel ? (
                  <Chip size="small" label={`Channel depth: ${selectedHistogramBitDepthLabel}`} variant="outlined" />
                ) : null}
                {selectedHistogramTotalPixelsLabel ? (
                  <Chip size="small" label={selectedHistogramTotalPixelsLabel} variant="outlined" />
                ) : null}
              </Box>

              {histogramFlavorOptions.length > 1 ? (
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Show
                  </Typography>
                  {map(histogramFlavorOptions, (flavorOption) => {
                    const checked = visibleHistogramFlavorNames.includes(flavorOption.flavor);
                    const disableLastVisibleOption = checked && visibleHistogramFlavorNames.length <= 1;

                    return (
                      <FormControlLabel
                        key={flavorOption.flavor}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={disableLastVisibleOption}
                            onChange={() => handleHistogramFlavorToggle(flavorOption.flavor)}
                            sx={{
                              color: flavorOption.color,
                              '&.Mui-checked': {
                                color: flavorOption.color,
                              },
                            }}
                          />
                        }
                        label={
                          <Box component="span" display="inline-flex" alignItems="center" gap={0.75}>
                            <Box
                              component="span"
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: flavorOption.color,
                                flex: '0 0 auto',
                              }}
                            />
                            {flavorOption.label}
                          </Box>
                        }
                        sx={{ mr: 0.5 }}
                      />
                    );
                  })}
                </Box>
              ) : null}

              {isLoadingHistogramPreview ? (
                <Box display="flex" alignItems="center" gap={1.5} sx={{ minHeight: histogramPreviewFigureHeight }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Fetching the histogram artifact for {selectedMarkerLabel} on{' '}
                    {selectedHistogramSlideDisplayLabel || selectedHistogramObservation.slideId}.
                  </Typography>
                </Box>
              ) : histogramPreviewFigure ? (
                <ThemedPlotlyFigure
                  figure={histogramPreviewFigure}
                  height={histogramPreviewFigureHeight}
                  onPlotlyInit={setHistogramChartEl}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No histogram artifact was found for this slide/channel. A multiplex histogram or normalization job has
                  not calculated it yet.
                </Typography>
              )}
            </Paper>
          ) : null}
        </>
      )}
    </Paper>
  );
};

export default QCMarkerNormalizationInsights;
